import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listLocationsMenu } from "@/lib/content.functions";

export function LocationsFooterMenu() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data: locations = [] } = useQuery({
    queryKey: ["footer-locations"],
    queryFn: () => listLocationsMenu(),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = locations.slice().sort((a, b) => {
      const ka = `${a.city} ${a.neighborhood ?? ""} ${a.service_label}`;
      const kb = `${b.city} ${b.neighborhood ?? ""} ${b.service_label}`;
      return ka.localeCompare(kb, "pt-BR");
    });
    if (!term) return list;
    return list.filter((l) =>
      `${l.city} ${l.neighborhood ?? ""} ${l.service_label} ${l.title}`
        .toLowerCase()
        .includes(term),
    );
  }, [locations, q]);

  if (locations.length === 0) return null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="inline-flex w-full items-center justify-between gap-3 rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] px-4 py-2.5 text-sm font-medium text-foreground transition hover:border-primary/60 hover:bg-ink/[0.06]"
      >
        <span className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          Atendimento por região
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
            {locations.length}
          </span>
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={open ? "rotate-180 transition" : "transition"}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden rounded-2xl border border-ink/10 bg-background/95 shadow-2xl backdrop-blur-xl">
          <div className="border-b border-ink/10 p-3">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar cidade, bairro ou serviço…"
              className="w-full rounded-lg border border-ink/10 bg-ink/[0.04] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary"
            />
          </div>
          <ul role="listbox" className="max-h-72 overflow-auto py-1">
            {filtered.length === 0 && (
              <li className="px-4 py-3 text-xs text-muted-foreground">Nenhuma região encontrada.</li>
            )}
            {filtered.map((l) => (
              <li key={l.id}>
                <Link
                  to="/servicos/$slug"
                  params={{ slug: l.slug }}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-muted-foreground transition hover:bg-ink/[0.05] hover:text-foreground"
                >
                  <span className="truncate">
                    <span className="text-foreground">{l.city}{l.neighborhood ? ` · ${l.neighborhood}` : ""}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{l.service_label}</span>
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0 text-primary/70">
                    <path d="M7 17L17 7" /><path d="M8 7h9v9" />
                  </svg>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
